import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Edit, Trash2, Users, RefreshCw, Eye, Filter, Download,
  Mail, Phone, Shield, Building2, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { usersAPI, lookupDataAPI, rolesAPI, leaveTypesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { collectAllPaginatedResults } from '../../utils/pagination';

const buildUserSchema = (isEditing, requirePassword) => {
  const passwordOptionalSchema = z.union([
    z.string().min(8, 'Password must be at least 8 characters'),
    z.literal('')
  ]).optional();

  const baseSchema = z.object({
    full_name: z.string().min(1, 'Full name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(1, 'Phone number is required'),
    password: passwordOptionalSchema,
    branch_id: z.string().optional(),
    reporting_to: z.string().optional().nullable(),
    roles: z.array(z.object({
      role_name: z.string(),
      permissions: z.array(z.string())
    })).optional(),
    is_active: z.boolean().default(true),
    is_school_user: z.boolean().default(false),
  });

  if (!isEditing || requirePassword) {
    return baseSchema.refine((data) => !!data.password, {
      message: 'Password is required',
      path: ['password'],
    });
  }

  return baseSchema;
};

const UsersManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [leaveCountsMap, setLeaveCountsMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userId: null, userName: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const lastQueryRef = useRef({ search: null, filterBranch: null, filterRole: null });

  const defaultValues = {
    full_name: '',
    email: '',
    phone: '',
    password: '',
    branch_id: '',
    reporting_to: '',
    roles: [],
    is_active: true,
    is_school_user: false,
    leave_counts: [],
  };

  const userSchema = useMemo(
    () => buildUserSchema(!!editingUser, showPasswordChange),
    [editingUser, showPasswordChange]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues,
  });

  const selectedRoles = watch('roles');
  const isSchoolUser = watch('is_school_user');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const nextQuery = { search: debouncedSearchTerm, filterBranch, filterRole };
    if (
      lastQueryRef.current.search === nextQuery.search &&
      lastQueryRef.current.filterBranch === nextQuery.filterBranch &&
      lastQueryRef.current.filterRole === nextQuery.filterRole
    ) {
      return;
    }
    lastQueryRef.current = nextQuery;
    loadUsers(nextQuery.search);
  }, [filterBranch, filterRole, debouncedSearchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterBranch, filterRole]);

  const loadInitialData = async () => {
    try {
      const [branchesData, rolesData, managersData] = await Promise.all([
        lookupDataAPI.getBranches(),
        rolesAPI.getRoles(),
        lookupDataAPI.getUsers()
      ]);
      
      const branchesArray = Array.isArray(branchesData) ? branchesData : branchesData.branches || [];
      const rolesArray = Array.isArray(rolesData) ? rolesData : rolesData.roles || [];
      const allManagers = Array.isArray(managersData) ? managersData : managersData.users || [];
      
      setBranches(branchesArray);
      setRoles(rolesArray);
      setManagers(allManagers.filter((manager) => !manager.is_school_user));
      // load leave types
      try {
        const lt = await leaveTypesAPI.getLeaveTypes();
        setLeaveTypes(Array.isArray(lt) ? lt : lt || []);
      } catch (e) {
        setLeaveTypes([]);
      }
      // initialize leaveCountsMap with zeros
      setLeaveCountsMap({});
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data', 'Please try again');
    }
  };

  const getActiveBranches = () => {
    const activeBranches = branches.filter(branch => branch.is_active);
    if (editingUser?.branch_id) {
      const currentBranch = branches.find(branch => branch.id === editingUser.branch_id);
      if (currentBranch && !currentBranch.is_active) {
        return [currentBranch, ...activeBranches];
      }
    }
    return activeBranches;
  };

  const loadUsers = async (search = '') => {
    try {
      setLoading(true);
      const { items } = await collectAllPaginatedResults(async (skip, limit) => {
        const params = { skip, limit };
        if (search) params.search = search;
        const response = await usersAPI.getUsers(params);
        return {
          items: response?.users || [],
          total: response?.total || 0,
        };
      });
      setUsers(items);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users', 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadUsers(debouncedSearchTerm),
        lookupDataAPI.getBranches()
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userData = {
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        branch_id: data.branch_id || null,
        reporting_to: data.is_school_user ? null : (data.reporting_to || null),
        roles: data.roles || [],
        is_active: data.is_active,
        is_school_user: Boolean(data.is_school_user),
        leave_counts: data.leave_counts || [],
      };

      if (!editingUser || (showPasswordChange && data.password)) {
        userData.password = data.password;
      }

      // attach leave_counts from leaveCountsMap
      userData.leave_counts = Object.keys(leaveCountsMap).map(k => ({ leave_type: k, total: Number(leaveCountsMap[k] || 0) }));

      if (editingUser) {
        await usersAPI.updateUser(editingUser.id, userData);
        toast.success('User updated successfully!');
      } else {
        await usersAPI.createUser(userData);
        toast.success('User created successfully!');
      }
      
      setShowForm(false);
      setEditingUser(null);
      setShowPasswordChange(false);
      reset();
      await loadUsers(debouncedSearchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save user';
      toast.error('Failed to save user', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowPasswordChange(false);
    reset({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone,
      branch_id: user.branch_id || '',
      reporting_to: user.reporting_to || '',
      roles: user.roles || [],
      is_active: user.is_active,
      is_school_user: Boolean(user.is_school_user),
      password: '',
    });
    setShowForm(true);
    (async () => {
      try {
        const details = await usersAPI.getUser(user.id);
        setEditingUser(details);
        setShowPasswordChange(false);
        // build leave counts map
        const lcMap = {};
        (details.leave_counts || []).forEach(lc => { lcMap[lc.leave_type || lc.code] = lc.total; });
        setLeaveCountsMap(lcMap);
        reset({
          full_name: details.full_name,
          email: details.email,
          phone: details.phone,
          branch_id: details.branch_id || '',
          reporting_to: details.reporting_to || '',
          roles: details.roles || [],
          is_active: details.is_active,
          is_school_user: Boolean(details.is_school_user),
          password: '',
        });
        setShowForm(true);
      } catch (err) {
        toast.error('Failed to load user details');
      }
    })();
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setShowPasswordChange(false);
    reset(defaultValues);
    setShowForm(true);
  };

  const availableManagers = useMemo(() => {
    return (managers || []).filter((manager) => manager.id !== editingUser?.id);
  }, [managers, editingUser]);

  const handleDelete = (user) => {
    setDeleteConfirm({ show: true, userId: user.id, userName: user.full_name });
  };

  const confirmDelete = async () => {
    try {
      await usersAPI.deleteUser(deleteConfirm.userId);
      toast.success('User deleted successfully!');
      await loadUsers(debouncedSearchTerm);
      setDeleteConfirm({ show: false, userId: null, userName: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      toast.error('Failed to delete user', errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, userId: null, userName: '' });
  };

  const handleView = (user) => {
    setViewingUser(user);
  };

  const getRoleColor = (roles) => {
    if (!roles || roles.length === 0) return 'bg-gray-100 text-gray-700 border-gray-200';
    
    const roleNames = Array.isArray(roles) ? roles.map(r => r.role_name) : [];
    if (roleNames.includes('super_admin')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (roleNames.includes('ffln_admin')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (roleNames.includes('branch_manager')) return 'bg-green-100 text-green-700 border-green-200';
    if (roleNames.includes('school_admin')) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = (user.full_name || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                           (user.email || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      const matchesBranch = !filterBranch || user.branch_id === filterBranch;
      const matchesRole = !filterRole || user.roles?.some(role => role.role_name === filterRole);
      return matchesSearch && matchesBranch && matchesRole;
    });
  }, [users, debouncedSearchTerm, filterBranch, filterRole]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const userStats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    admins: users.filter(u => u.roles?.some(r => ['super_admin', 'ffln_admin'].includes(r.role_name))).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
            <p className="text-gray-600 mt-2">Manage system users and permissions</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add User
            </Button>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: userStats.total, color: 'blue' },
          { label: 'Active Users', value: userStats.active, color: 'green' },
          { label: 'Inactive Users', value: userStats.inactive, color: 'red' },
          { label: 'Admins', value: userStats.admins, color: 'purple' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-4">
                <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingUser ? 'Edit User' : 'Add New User'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="full_name">Full Name</Label>
                    <Controller
                      name="full_name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter full name"
                          {...field}
                          error={errors.full_name?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="email"
                          placeholder="Enter email"
                          {...field}
                          error={errors.email?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter phone number"
                          {...field}
                          error={errors.phone?.message}
                        />
                      )}
                    />
                  </div>

                  {!editingUser && (
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="password"
                            placeholder="Enter password (minimum 8 characters)"
                            {...field}
                            error={errors.password?.message}
                          />
                        )}
                      />
                    </div>
                  )}

                  {editingUser && (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowPasswordChange(prev => !prev)}
                      >
                        {showPasswordChange ? 'Cancel Password Change' : 'Change Password'}
                      </Button>
                      {showPasswordChange && (
                        <div>
                          <Label htmlFor="password">New Password</Label>
                          <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="password"
                                placeholder="Enter new password (minimum 8 characters)"
                                {...field}
                                error={errors.password?.message}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="branch_id">Branch</Label>
                    <Controller
                      name="branch_id"
                      control={control}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="">Select Branch</option>
                          {getActiveBranches().map(branch => (
                            <option key={branch.id} value={branch.id}>
                              {branch.branch_name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </div>

                  <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3">
                    <Controller
                      name="is_school_user"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="is_school_user"
                          type="checkbox"
                          checked={Boolean(field.value)}
                          onChange={(event) => field.onChange(event.target.checked)}
                          className="mt-1 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                      )}
                    />
                    <div>
                      <Label htmlFor="is_school_user" className="cursor-pointer">Is school user?</Label>
                      <p className="text-sm text-gray-500 mt-1">Enable meal request access for this user from the dashboard.</p>
                    </div>
                  </div>

                  {!isSchoolUser && (
                    <div>
                      <Label htmlFor="reporting_to">Reporting Manager</Label>
                      <Controller
                        name="reporting_to"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} value={field.value || ''}>
                            <option value="">Select reporting manager</option>
                            {availableManagers.map((manager) => (
                              <option key={manager.id} value={manager.id}>
                                {manager.full_name} ({manager.email})
                              </option>
                            ))}
                          </Select>
                        )}
                      />
                      <p className="text-sm text-gray-500 mt-1">Used to route leave approvals for this employee.</p>
                    </div>
                  )}

                  <div>
                    <Label>Roles</Label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {roles.map(role => (
                        <label key={role.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedRoles?.some(r => r.role_name === role.role_name) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newRoles = [...(selectedRoles || []), { 
                                  role_name: role.role_name, 
                                  permissions: role.permissions || [] 
                                }];
                                setValue('roles', newRoles);
                              } else {
                                const newRoles = selectedRoles?.filter(r => r.role_name !== role.role_name) || [];
                                setValue('roles', newRoles);
                              }
                            }}
                            className="mr-2 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm">{role.role_name.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                    <div>
                      <Label>Leave Counts</Label>
                      <div className="space-y-2 border border-gray-100 rounded-lg p-3">
                        {leaveTypes.length ? leaveTypes.map((lt) => (
                          <div key={lt.code} className="flex items-center gap-3">
                            <div className="flex-1 text-sm text-slate-700">{lt.label} ({lt.code})</div>
                            <div className="w-32">
                              <Input
                                type="number"
                                min="0"
                                value={leaveCountsMap[lt.code] ?? ''}
                                onChange={(e) => setLeaveCountsMap(prev => ({ ...prev, [lt.code]: e.target.value }))}
                              />
                            </div>
                          </div>
                        )) : (
                          <div className="text-sm text-slate-500">No leave types defined yet.</div>
                        )}
                      </div>
                    </div>

                  <div>
                    <Label htmlFor="is_active">Status</Label>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value ? 'true' : 'false'}
                          onChange={(event) => field.onChange(event.target.value === 'true')}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      {editingUser ? 'Update' : 'Create'} User
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingUser(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Users List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Users</CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Select
                  value={filterBranch}
                  onChange={(e) => setFilterBranch(e.target.value)}
                  className="sm:w-48"
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </Select>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="sm:w-48"
                >
                  <option value="">All Roles</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="ffln_admin">FFLN Admin</option>
                  <option value="branch_manager">Branch Manager</option>
                  <option value="school_admin">School Admin</option>
                </Select>
                <Button variant="outline" size="sm">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {paginatedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No users found</p>
                  </div>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                      className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{user.full_name}</h3>
                            <Badge variant={user.is_active ? 'success' : 'error'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {user.roles && user.roles.length > 0 && (
                              <Badge className={getRoleColor(user.roles)}>
                                {user.roles.map(r => r.role_name).join(', ')}
                              </Badge>
                            )}
                            {user.is_school_user && (
                              <Badge variant="info">School User</Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Mail size={14} className="mr-1" />
                              {user.email}
                            </div>
                            <div className="flex items-center">
                              <Phone size={14} className="mr-1" />
                              {user.phone}
                            </div>
                            <div className="flex items-center">
                              <Building2 size={14} className="mr-1" />
                              {branches.find(b => b.id === user.branch_id)?.branch_name || 'No Branch'}
                            </div>
                          </div>

                          <div className="mt-2 text-xs text-gray-500">
                            Last login: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => handleView(user)}>
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(user)}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(user)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-100 pt-4 mt-4 gap-3">
                <p className="text-sm text-gray-500">
                  Showing {filteredUsers.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* User Details Modal */}
      {viewingUser && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingUser(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{viewingUser.full_name}</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingUser(null)}>
                ×
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{viewingUser.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{viewingUser.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Branch</p>
                <p className="font-medium">
                  {branches.find(b => b.id === viewingUser.branch_id)?.branch_name || 'No Branch'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={viewingUser.is_active ? 'success' : 'error'}>
                  {viewingUser.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Roles</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {viewingUser.roles?.map((role, index) => (
                    <Badge key={index} className={getRoleColor([role])}>
                      {role.role_name}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Login</p>
                <p className="font-medium">
                  {viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the user <strong>"{deleteConfirm.userName}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will permanently remove the user account.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete User
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default UsersManagement;
